import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../styles/midnight-lab.css';

function FTCPage() {
  const [visible, setVisible] = useState({});
  const [countValues, setCountValues] = useState({ seasons: 0, robots: 0, awards: 0 });
  const sectionRefs = useRef({});

  useEffect(() => {
    // Count up animation
    const targets = { seasons: 5, robots: 12, awards: 15 };
    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;

    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const easeOut = 1 - Math.pow(1 - progress, 3);

      setCountValues({
        seasons: Math.floor(targets.seasons * easeOut),
        robots: Math.floor(targets.robots * easeOut),
        awards: Math.floor(targets.awards * easeOut)
      });

      if (currentStep >= steps) {
        clearInterval(interval);
        setCountValues(targets);
      }
    }, stepDuration);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const observers = Object.keys(sectionRefs.current).map((key) => {
      return new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisible((prev) => ({ ...prev, [key]: true }));
          }
        },
        { threshold: 0.1 }
      );
    });

    Object.keys(sectionRefs.current).forEach((key, index) => {
      if (sectionRefs.current[key]) {
        observers[index].observe(sectionRefs.current[key]);
      }
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  const navigationCards = [
    {
      title: 'Our Robot',
      description: 'Explore our current and past FTC robots',
      link: '/ftc/robot',
      color: 'var(--color-frozen-lake)'
    },
    {
      title: 'Achievements',
      description: 'View our competition results and awards',
      link: '/ftc/achievements',
      color: 'var(--color-yellow-green)'
    },
    {
      title: 'Team',
      description: 'Meet our FTC team members',
      link: '/ftc/team',
      color: 'var(--color-frozen-lake)'
    }
  ];

  const features = [
    {
      title: 'Design & Engineering',
      description: 'Student-led design process using CAD, rapid prototyping, and iterative development'
    },
    {
      title: 'Programming & Controls',
      description: 'Java-based autonomous routines, vision processing, and advanced control systems'
    },
    {
      title: 'Strategy & Competition',
      description: 'Data-driven strategy, scouting, and alliance selection for competitive success'
    },
    {
      title: 'Outreach & Community',
      description: 'STEM demonstrations, mentoring younger teams, and community engagement'
    }
  ];

  return (
    <div style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      {/* Navigation Cards Section */}
      <section
        ref={(el) => (sectionRefs.current['navigation'] = el)}
        style={{
          padding: 'var(--space-24) var(--space-4)',
          maxWidth: '1400px',
          margin: '0 auto',
          opacity: visible.navigation ? 1 : 0,
          transform: visible.navigation ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
        }}
      >
        <h2 style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--space-12)',
          textAlign: 'center',
          letterSpacing: '-0.02em'
        }}>
          Explore FTC
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 'var(--space-8)',
          marginBottom: 'var(--space-16)'
        }}>
          {navigationCards.map((card, index) => (
            <Link
              key={index}
              to={card.link}
              style={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
                padding: 'var(--space-8)',
                borderBottom: '2px solid rgba(0, 16, 17, 0.1)',
                transition: 'all var(--transition-base)',
                opacity: visible.navigation ? 1 : 0,
                transform: visible.navigation
                  ? 'translateY(0)'
                  : 'translateY(30px)',
                animation: visible.navigation
                  ? `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                  : 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.borderBottomColor = card.color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderBottomColor = 'rgba(0, 16, 17, 0.1)';
              }}
            >
              <h3 style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-2xl)',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
                marginBottom: 'var(--space-2)'
              }}>
                {card.title}
              </h3>
              <p style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-base)',
                color: 'var(--color-text-secondary)',
                lineHeight: 1.7
              }}>
                {card.description}
              </p>
              <div style={{
                marginTop: 'var(--space-4)',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
                fontWeight: 600,
                color: card.color,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)'
              }}>
                Learn More
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* What We Do Section */}
      <section
        ref={(el) => (sectionRefs.current['features'] = el)}
        style={{
          padding: 'var(--space-24) var(--space-4)',
          maxWidth: '1400px',
          margin: '0 auto',
          borderTop: '1px solid rgba(0, 16, 17, 0.1)',
          opacity: visible.features ? 1 : 0,
          transform: visible.features ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
        }}
      >
        <h2 style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--space-4)',
          textAlign: 'center',
          letterSpacing: '-0.02em'
        }}>
          What We Do
        </h2>
        <p style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-lg)',
          color: 'var(--color-text-secondary)',
          textAlign: 'center',
          marginBottom: 'var(--space-16)',
          maxWidth: '700px',
          margin: '0 auto var(--space-16)'
        }}>
          FTC combines hands-on engineering with competitive robotics, developing real-world skills
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 'var(--space-8)'
        }}>
          {features.map((feature, index) => (
            <div
              key={index}
              style={{
                padding: 'var(--space-6) 0',
                borderBottom: '1px solid rgba(0, 16, 17, 0.1)',
                opacity: visible.features ? 1 : 0,
                transform: visible.features
                  ? 'translateY(0)'
                  : 'translateY(30px)',
                transition: `opacity 0.6s ease-out ${0.2 + index * 0.1}s, transform 0.6s ease-out ${0.2 + index * 0.1}s`
              }}
            >
              <h3 style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-xl)',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
                marginBottom: 'var(--space-3)'
              }}>
                {feature.title}
              </h3>
              <p style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-base)',
                color: 'var(--color-text-secondary)',
                lineHeight: 1.7
              }}>
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section
        ref={(el) => (sectionRefs.current['stats'] = el)}
        style={{
          padding: 'var(--space-24) var(--space-4)',
          maxWidth: '1400px',
          margin: '0 auto',
          borderTop: '1px solid rgba(0, 16, 17, 0.1)',
          opacity: visible.stats ? 1 : 0,
          transform: visible.stats ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
        }}
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 'var(--space-8)',
          textAlign: 'center'
        }}>
          <div>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'clamp(3rem, 5vw, 5rem)',
              fontWeight: 700,
              color: 'var(--color-frozen-lake)',
              marginBottom: 'var(--space-2)',
              transition: 'transform 0.3s ease'
            }}>
              {countValues.seasons}+
            </div>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Seasons Competing
            </div>
          </div>
          <div>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'clamp(3rem, 5vw, 5rem)',
              fontWeight: 700,
              color: 'var(--color-frozen-lake)',
              marginBottom: 'var(--space-2)',
              transition: 'transform 0.3s ease'
            }}>
              {countValues.robots}
            </div>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Robots Built
            </div>
          </div>
          <div>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'clamp(3rem, 5vw, 5rem)',
              fontWeight: 700,
              color: 'var(--color-frozen-lake)',
              marginBottom: 'var(--space-2)',
              transition: 'transform 0.3s ease'
            }}>
              {countValues.awards}+
            </div>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Awards Won
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        ref={(el) => (sectionRefs.current['cta'] = el)}
        style={{
          padding: 'var(--space-24) var(--space-4)',
          maxWidth: '1400px',
          margin: '0 auto',
          borderTop: '1px solid rgba(0, 16, 17, 0.1)',
          textAlign: 'center',
          opacity: visible.cta ? 1 : 0,
          transform: visible.cta ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
        }}
      >
        <h2 style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--space-6)',
          letterSpacing: '-0.02em'
        }}>
          Join Our FTC Team
        </h2>
        <p style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-lg)',
          color: 'var(--color-text-secondary)',
          lineHeight: 1.7,
          maxWidth: '600px',
          margin: '0 auto var(--space-8)'
        }}>
          Whether you're interested in engineering, programming, or competition strategy, 
          there's a place for you on our FTC team.
        </p>
        <div style={{
          display: 'flex',
          gap: 'var(--space-4)',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <Link
            to="/join"
            style={{
              padding: '16px 40px',
              backgroundColor: 'var(--color-text-primary)',
              color: 'var(--color-text-light)',
              fontSize: 'var(--text-base)',
              fontWeight: 600,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              textDecoration: 'none',
              fontFamily: 'var(--font-sans)',
              border: '2px solid var(--color-text-primary)',
              transition: 'all var(--transition-base)',
              display: 'inline-block'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--color-frozen-lake)';
              e.target.style.borderColor = 'var(--color-frozen-lake)';
              e.target.style.color = 'var(--color-text-primary)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--color-text-primary)';
              e.target.style.borderColor = 'var(--color-text-primary)';
              e.target.style.color = 'var(--color-text-light)';
            }}
          >
            Join the Team
          </Link>
          <Link
            to="/ftc/team"
            style={{
              padding: '16px 40px',
              backgroundColor: 'transparent',
              color: 'var(--color-text-primary)',
              fontSize: 'var(--text-base)',
              fontWeight: 600,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              textDecoration: 'none',
              fontFamily: 'var(--font-sans)',
              border: '2px solid var(--color-text-primary)',
              transition: 'all var(--transition-base)',
              display: 'inline-block'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--color-text-primary)';
              e.target.style.color = 'var(--color-text-light)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = 'var(--color-text-primary)';
            }}
          >
            Meet the Team
          </Link>
        </div>
      </section>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default FTCPage;

